/**
 * pages/AdminAccess.tsx — the access-administration module: groups, what each confers, and who is in them.
 *
 * The question this screen exists to answer is not "what groups are there" but **"where does this
 * permission come from"**. That is why the unit of display is a facet row with its SOURCE named on it,
 * rather than a tidy per-person summary: a summary that says "can reach Tropical Sun" and cannot say which
 * group said so is a summary nobody can act on, and revoking it becomes guesswork across every group in the
 * estate. So every chip names its group, including the chips inside that group's own row. The repetition is
 * deliberate: a chip is read, screenshotted and quoted on its own.
 *
 * **The dead-grant warning is the second reason this screen exists.** §3.2 scope roles are scoped: a role
 * granted for a business unit the group gives no scope ACCESS to grants exactly nothing, because Gate 3
 * refuses before the role is ever consulted. The server returns that as a warning on the write, which the
 * administrator who made it sees once and everybody afterwards does not — so it is recomputed here, from
 * the rows themselves, every time the screen is drawn.
 *
 * **Every operation is the superuser's**, and the buttons are drawn from `isSuperuser` rather than from
 * `sections.access.act`. That is not a shortcut: `permissions/delegation.ts` lists the administrative routes
 * a delegate may reach, every entry in it is a GET, and absence means superuser-only. A button drawn from
 * the section would be offered to a delegate and then always 403 — a worse lie than a missing button. A
 * delegate therefore sees this whole screen, in full, with no controls at all.
 *
 * **Nothing here subtracts.** §3.1 scope access unions across a subject's groups and has no deny form, so
 * removing a row — or removing a person from the group — is the only lever that narrows anything.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type AdminUser, type ScopeSummary } from "../lib/api";
import { formatDateTime, humanise, roleLabel } from "../lib/format";
import {
  Badge,
  Banner,
  Button,
  DeniedState,
  ErrorState,
  LoadingState,
  Modal,
  Page,
  Table,
  TextField,
  useToast,
  type Column,
} from "../components/ui";
import { AccessGroupEditor } from "./AccessGroupEditor";
import type {
  AdminAccessPorts,
  BusinessUnitOption,
  GroupFacets,
  GroupRow,
  PolicyAuditRow,
  PolicyWriteResult,
} from "./accessTypes";

export type { GroupFacets, GroupRow } from "./accessTypes";

/* ---------------------------------------------------------------------------------------------------
 * The default ports — one place where this screen touches the network
 * ------------------------------------------------------------------------------------------------- */

const groupPath = (groupId: string): string => `/api/admin/groups/${encodeURIComponent(groupId)}`;

/** Every facet write answers `{changed, warnings}`; a 204 answers nothing. Both become one shape here. */
async function written(result: Promise<PolicyWriteResult | undefined>): Promise<PolicyWriteResult> {
  return (await result) ?? { changed: 1, warnings: [] };
}

const apiPorts: AdminAccessPorts = {
  loadGroups: async (signal) => {
    const list = await api.get<{ readonly groups: readonly GroupRow[] }>("/api/admin/groups", { signal });
    // Settled, not awaited together: one group whose detail is refused or missing used to collapse the
    // entire screen into an error state. A named unreadable row is a far better answer than no rows.
    const settled = await Promise.allSettled(
      list.groups.map((group) => api.get<GroupFacets>(groupPath(group.id), { signal })),
    );
    return settled.map((outcome, index) => {
      const group = list.groups[index] as GroupRow;
      if (outcome.status === "fulfilled") return outcome.value;
      const reason = outcome.reason;
      return {
        group,
        members: [],
        scopeAccess: [],
        scopeRoles: [],
        resourceAllow: [],
        resourceDeny: [],
        resourceHide: [],
        adminSections: [],
        unreadable: reason instanceof ApiError ? reason.userMessage : "This group could not be read.",
      };
    });
  },

  loadUnits: async (signal) => {
    const response = await api.get<{ readonly businessUnits: readonly BusinessUnitOption[] }>(
      "/api/admin/business-units",
      { signal },
    );
    return response.businessUnits;
  },

  loadUsers: async (signal) => {
    const response = await api.get<{ readonly users: readonly AdminUser[] }>("/api/admin/users", { signal });
    return response.users;
  },

  loadPolicyAudit: async (signal) => {
    const response = await api.get<{ readonly entries: readonly PolicyAuditRow[] }>(
      "/api/admin/policy-audit",
      { query: { limit: 100 }, signal },
    );
    return response.entries;
  },

  createGroup: async (name, description) => {
    const response = await api.post<{ readonly group: GroupRow }>("/api/admin/groups", {
      name,
      ...(description === null ? {} : { description }),
    });
    if (!response) throw new ApiError(502, "internal_error", "the group was not returned", null);
    return response.group;
  },

  updateGroup: (groupId, name, description) =>
    written(api.patch<PolicyWriteResult>(groupPath(groupId), { name, description })),

  deleteGroup: async (groupId) => {
    await api.delete(groupPath(groupId));
  },

  addMember: (groupId, userId) =>
    written(api.post<PolicyWriteResult>(`${groupPath(groupId)}/members`, { userId })),
  removeMember: (groupId, userId) =>
    written(api.delete<PolicyWriteResult>(`${groupPath(groupId)}/members/${encodeURIComponent(userId)}`)),

  grantScopeAccess: (groupId, buId) =>
    written(api.post<PolicyWriteResult>(`${groupPath(groupId)}/scope-access`, { buId })),
  // Revocations take their arguments in the query string, not a body — a DELETE with a body is refused.
  revokeScopeAccess: (groupId, buId) =>
    written(api.delete<PolicyWriteResult>(`${groupPath(groupId)}/scope-access`, { query: { buId } })),

  grantScopeRole: (groupId, buId, role) =>
    written(api.post<PolicyWriteResult>(`${groupPath(groupId)}/scope-roles`, { buId, role })),
  revokeScopeRole: (groupId, buId, role) =>
    written(api.delete<PolicyWriteResult>(`${groupPath(groupId)}/scope-roles`, { query: { buId, role } })),

  addResourceRule: (groupId, list, buId, kind, pattern) =>
    written(api.post<PolicyWriteResult>(`${groupPath(groupId)}/resource-rules`, { list, buId, kind, pattern })),
  removeResourceRule: (groupId, list, buId, kind, pattern) =>
    written(
      api.delete<PolicyWriteResult>(`${groupPath(groupId)}/resource-rules`, {
        query: { list, buId, kind, pattern },
      }),
    ),

  setSection: (groupId, section, view, act) =>
    written(api.put<PolicyWriteResult>(`${groupPath(groupId)}/sections`, { section, view, act })),
  removeSection: (groupId, section) =>
    written(api.delete<PolicyWriteResult>(`${groupPath(groupId)}/sections`, { query: { section } })),
};

/* ---------------------------------------------------------------------------------------------------
 * The dead grant
 * ------------------------------------------------------------------------------------------------- */

export interface DeadRole {
  readonly groupName: string;
  readonly buId: string;
  readonly role: string;
}

/**
 * A role granted for a business unit the same group gives no scope access to.
 *
 * Computed per group rather than across the estate, and that is the correct reading: §3.1 access unions
 * across a subject's groups, so another group MIGHT supply the missing access for a particular person. The
 * warning therefore says what it can prove — this grant, on its own, does nothing — and does not claim the
 * person cannot reach the business unit.
 */
export function deadRoles(facets: GroupFacets): readonly DeadRole[] {
  const reachable = new Set(facets.scopeAccess.map((row) => row.bu_id));
  return facets.scopeRoles
    .filter((row) => !reachable.has(row.bu_id))
    .map((row) => ({ groupName: facets.group.name, buId: row.bu_id, role: row.role }));
}

/**
 * What deleting a group takes away, in the words the confirmation must use.
 *
 * `06-modules.md` §2.2 makes this an explicit MUST: deleting a carrier is the fastest way to remove access
 * from many people at once, and it must not be a bare "are you sure?". Everything below is already loaded,
 * so the sentence costs no request.
 */
export function blastRadius(facets: GroupFacets, unitName: (id: string) => string): string {
  const people = facets.members.length;
  const losses: string[] = [];

  if (facets.scopeAccess.length > 0) {
    losses.push(
      `access to ${facets.scopeAccess.map((row) => unitName(row.bu_id)).join(", ")}`,
    );
  }
  if (facets.scopeRoles.length > 0) {
    losses.push(
      `${facets.scopeRoles.map((row) => `${roleLabel(row.role)} in ${unitName(row.bu_id)}`).join(", ")}`,
    );
  }
  const rules = facets.resourceAllow.length + facets.resourceDeny.length + facets.resourceHide.length;
  if (rules > 0) losses.push(`${rules} resource rule${rules === 1 ? "" : "s"}`);
  if (facets.adminSections.length > 0) {
    losses.push(
      `the ${facets.adminSections.map((row) => humanise(row.section)).join(", ")} administrative section${facets.adminSections.length === 1 ? "" : "s"}`,
    );
  }

  const who =
    people === 0
      ? "Nobody is in this group"
      : `${people} ${people === 1 ? "person" : "people"} ${people === 1 ? "is" : "are"} in this group`;

  if (losses.length === 0) {
    return `${who}, and it confers nothing — deleting it takes nothing away.`;
  }
  if (people === 0) {
    return `${who}, so nobody loses anything today. It confers ${losses.join("; ")} — and would do so the moment somebody joined.`;
  }
  return `${who}, and each of them loses ${losses.join("; ")} — unless another group grants the same thing, which access unions from.`;
}

/* ---------------------------------------------------------------------------------------------------
 * The estate table
 * ------------------------------------------------------------------------------------------------- */

interface FacetEntry {
  readonly id: string;
  readonly groupName: string;
  readonly facet: string;
  readonly scope: string;
  readonly detail: string;
  readonly effect: "grants" | "denies" | "hides";
}

export function flatten(
  facets: readonly GroupFacets[],
  buName: (id: string) => string,
): readonly FacetEntry[] {
  const entries: FacetEntry[] = [];
  for (const group of facets) {
    const name = group.group.name;
    for (const row of group.scopeAccess) {
      entries.push({
        id: `${group.group.id}:access:${row.bu_id}`,
        groupName: name,
        facet: "Scope access",
        scope: buName(row.bu_id),
        detail: "Can reach this business unit",
        effect: "grants",
      });
    }
    for (const row of group.scopeRoles) {
      entries.push({
        id: `${group.group.id}:role:${row.bu_id}:${row.role}`,
        groupName: name,
        facet: "Delegated role",
        scope: buName(row.bu_id),
        detail: roleLabel(row.role),
        effect: "grants",
      });
    }
    for (const row of group.resourceAllow) {
      entries.push({
        id: `${group.group.id}:allow:${row.bu_id}:${row.kind}:${row.pattern}`,
        groupName: name,
        facet: `Resource allow — ${humanise(row.kind)}`,
        scope: buName(row.bu_id),
        detail: row.pattern,
        effect: "grants",
      });
    }
    for (const row of group.resourceDeny) {
      entries.push({
        id: `${group.group.id}:deny:${row.bu_id}:${row.kind}:${row.pattern}`,
        groupName: name,
        facet: `Resource deny — ${humanise(row.kind)}`,
        scope: buName(row.bu_id),
        detail: row.pattern,
        effect: "denies",
      });
    }
    for (const row of group.resourceHide) {
      entries.push({
        id: `${group.group.id}:hide:${row.bu_id}:${row.kind}:${row.pattern}`,
        groupName: name,
        facet: `Hidden from lists — ${humanise(row.kind)}`,
        scope: buName(row.bu_id),
        detail: row.pattern,
        effect: "hides",
      });
    }
    for (const row of group.adminSections) {
      entries.push({
        id: `${group.group.id}:section:${row.section}`,
        groupName: name,
        facet: "Admin section",
        // Sections are instance-level: they belong to no business unit, and pretending otherwise here
        // would be the forbidden shape of a scope attached to a thing that has none.
        scope: "Whole instance",
        detail: `${humanise(row.section)} — ${row.can_act === 1 ? "view and change" : "view only"}`,
        effect: "grants",
      });
    }
  }
  return entries;
}

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

/** One carrier, summarised for the list the module contract requires: name, members, and what it confers. */
interface CarrierRow {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly members: number;
  readonly units: string;
  readonly roles: number;
  readonly rules: number;
  readonly sections: string;
  readonly dead: number;
  readonly unreadable: string | null;
}

export interface AdminAccessProps {
  readonly canView: boolean;
  readonly canAct: boolean;
  /** From `/api/me`, used to name a business unit when the administrator cannot read the units endpoint. */
  readonly scopes?: readonly ScopeSummary[];
  readonly ports?: AdminAccessPorts;
}

export function AdminAccess({
  canView,
  canAct,
  scopes = [],
  ports = apiPorts,
}: AdminAccessProps): JSX.Element {
  const toast = useToast();
  const [facets, setFacets] = useState<readonly GroupFacets[] | null>(null);
  const [units, setUnits] = useState<readonly BusinessUnitOption[]>([]);
  const [users, setUsers] = useState<readonly AdminUser[]>([]);
  const [trail, setTrail] = useState<readonly PolicyAuditRow[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState("");
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<GroupFacets | null>(null);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!canView) return;
    const controller = new AbortController();
    let live = true;
    setFacets(null);
    setError(null);

    ports
      .loadGroups(controller.signal)
      .then((loaded) => {
        if (live) setFacets(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    // Three best-effort reads. Each belongs to a different administrative section, so a delegate holding
    // only `access` is refused them — and the screen degrades to ids and no member picker rather than
    // failing outright. This is the established pattern on the People screen.
    const optional = <T,>(promise: Promise<T>, apply: (value: T) => void): void => {
      promise.then((value) => { if (live) apply(value); }).catch(() => undefined);
    };
    optional(ports.loadUnits(controller.signal), setUnits);
    optional(ports.loadUsers(controller.signal), (loaded) => setUsers(loaded as readonly AdminUser[]));
    optional(ports.loadPolicyAudit(controller.signal), setTrail);

    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, canView, attempt]);

  if (!canView) {
    return (
      <Page title="Access">
        <DeniedState body="Reading who has access is its own grant. Ask an administrator to add you to a group that carries the access section." />
      </Page>
    );
  }

  // A business unit outside this administrator's own reach is named by id rather than hidden: the row
  // exists, it grants something, and concealing it would make the estate look smaller than it is.
  const buName = (id: string): string =>
    units.find((unit) => unit.id === id)?.displayName ??
    scopes.find((scope) => scope.id === id)?.displayName ??
    id;

  // The units endpoint belongs to another section, so when it is refused the pickers fall back to the
  // scopes this administrator can reach. Fewer options, never wrong ones.
  const unitOptions: readonly BusinessUnitOption[] =
    units.length > 0
      ? units
      : scopes.map((scope) => ({ id: scope.id, displayName: scope.displayName, enabled: true }));

  const groups = facets ?? [];
  const entries = flatten(groups, buName);
  const needle = search.trim().toLowerCase();
  const matches = (...fields: readonly string[]): boolean =>
    needle === "" || fields.some((field) => field.toLowerCase().includes(needle));

  const visible = entries.filter((entry) =>
    matches(entry.groupName, entry.facet, entry.scope, entry.detail),
  );

  const warnings = groups.flatMap(deadRoles);
  const unreadable = groups.filter((group) => group.unreadable !== undefined);

  const carriers: readonly CarrierRow[] = groups
    .map((group) => ({
      id: group.group.id,
      name: group.group.name,
      description: group.group.description ?? "",
      members: group.members.length,
      units: group.scopeAccess.map((row) => buName(row.bu_id)).join(", "),
      roles: group.scopeRoles.length,
      rules: group.resourceAllow.length + group.resourceDeny.length + group.resourceHide.length,
      sections: group.adminSections.map((row) => humanise(row.section)).join(", "),
      dead: deadRoles(group).length,
      unreadable: group.unreadable ?? null,
    }))
    .filter((row) => matches(row.name, row.description, row.units, row.sections));

  const facetsFor = (groupId: string): GroupFacets | undefined =>
    groups.find((group) => group.group.id === groupId);

  const carrierColumns: readonly Column<CarrierRow>[] = [
    {
      key: "name",
      header: "Group",
      type: "text",
      value: (row) => row.name,
      render: (row) => (
        <span>
          <strong>{row.name}</strong>
          {row.unreadable === null ? null : <Badge tone="warning">Unreadable</Badge>}
          {row.dead === 0 ? null : <Badge tone="warning">{row.dead} inert</Badge>}
        </span>
      ),
    },
    {
      key: "members",
      header: "Members",
      type: "number",
      numeric: true,
      value: (row) => row.members,
    },
    {
      key: "units",
      header: "Business units it reaches",
      type: "text",
      value: (row) => row.units,
      render: (row) => (row.units === "" ? <span className="field__hint">None</span> : <span>{row.units}</span>),
    },
    { key: "roles", header: "Roles", type: "number", numeric: true, value: (row) => row.roles },
    { key: "rules", header: "Resource rules", type: "number", numeric: true, value: (row) => row.rules, hideOnMobile: true },
    {
      key: "sections",
      header: "Admin sections",
      type: "text",
      value: (row) => row.sections,
      render: (row) =>
        row.sections === "" ? <span className="field__hint">None</span> : <span>{row.sections}</span>,
      hideOnMobile: true,
    },
    ...(canAct
      ? [
          {
            key: "actions",
            actions: true,
            header: "Actions",
            type: "text" as const,
            sortable: false,
            value: () => "",
            render: (row: CarrierRow) => (
              <span className="row">
                <Button variant="secondary" size="sm" onClick={() => setOpenGroup(row.id)}>
                  Manage
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    const found = facetsFor(row.id);
                    if (found) setDeleting(found);
                  }}
                >
                  Delete
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  const open = openGroup === null ? undefined : facetsFor(openGroup);

  return (
    <Page
      title="Access"
      lede="Every permission in this application is carried by a group. There is no permission column on an account and there never will be — so every row below names the group it comes from, and removing that row is how it is taken away."
      actions={
        canAct ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New group
          </Button>
        ) : undefined
      }
    >
      {unreadable.length === 0 ? null : (
        <Banner tone="danger">
          {unreadable.length === 1 ? "One group could not be read" : `${unreadable.length} groups could not be read`}:{" "}
          {unreadable.map((group) => group.group.name).join(", ")}. What they confer is missing from the table
          below, so treat it as incomplete until this is resolved.
        </Banner>
      )}

      {warnings.length === 0 ? null : (
        <Banner tone="warning">
          {warnings.length === 1 ? "One grant does nothing" : `${warnings.length} grants do nothing`}:{" "}
          {warnings
            .map(
              (warning) =>
                `“${warning.groupName}” grants ${roleLabel(warning.role)} in ${buName(warning.buId)} but gives that group no access to it`,
            )
            .join("; ")}
          . A role is only ever consulted after scope access is granted, so these have no effect until the
          matching scope access is added to the same group — or the role is removed.
        </Banner>
      )}

      {canAct ? null : (
        <Banner tone="info">
          You can read the access model but not change it. Every administrative change here is the
          superuser&rsquo;s alone — a hard rail of the permission model, not a grant somebody forgot to give
          you — so the controls are absent rather than shown and refused.
        </Banner>
      )}

      <TextField
        label="Search"
        value={search}
        onChange={setSearch}
        type="search"
        placeholder="Group, business unit, repository pattern"
      />

      {error !== null ? (
        error.isPermissionDenied ? (
          <DeniedState body={error.userMessage} />
        ) : (
          <ErrorState message={error.userMessage} traceId={error.traceId} onRetry={reload} />
        )
      ) : null}

      {error === null && facets === null ? <LoadingState label="Loading groups" /> : null}

      {facets !== null ? (
        <>
          <Table
            caption="Groups"
            columns={carrierColumns}
            rows={carriers}
            rowKey={(row) => row.id}
            defaultSort={{ key: "name", direction: "ascending" }}
            titleKey="name"
            mobile="cards"
            empty={{
              title: groups.length === 0 ? "No groups" : "Nothing matches that search",
              body:
                groups.length === 0
                  ? "Groups are the only carrier of permission here. Until one exists, nobody can reach anything — not even a business unit that has been created and enabled."
                  : "Clear the search to see every group again.",
              ...(groups.length === 0 && canAct
                ? {
                    action: (
                      <Button variant="primary" onClick={() => setCreating(true)}>
                        New group
                      </Button>
                    ),
                  }
                : {}),
            }}
          />

          <Table
            caption="Permissions by source group"
            columns={estateColumns}
            rows={visible}
            rowKey={(entry) => entry.id}
            defaultSort={{ key: "groupName", direction: "ascending" }}
            titleKey="detail"
            mobile="cards"
            empty={{
              title: entries.length === 0 ? "No group grants anything yet" : "Nothing matches that search",
              body:
                entries.length === 0
                  ? "A group with no facet rows is a group that grants nothing — which is the correct default. Open a group and grant it scope access to let its members reach a business unit."
                  : "Clear the search to see every facet row again.",
            }}
          />

          <PolicyTrail entries={trail} buName={buName} />
        </>
      ) : null}

      {open === undefined ? null : (
        <AccessGroupEditor
          facets={open}
          units={unitOptions}
          users={users}
          ports={ports}
          onClose={() => setOpenGroup(null)}
          onChanged={reload}
        />
      )}

      <Modal
        open={creating}
        title="New group"
        description="A group is the only carrier of permission here. It confers nothing until you grant it something."
        onClose={() => setCreating(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy || newName.trim() === ""}
              onClick={() => {
                setBusy(true);
                ports
                  .createGroup(newName.trim(), newDescription.trim() === "" ? null : newDescription.trim())
                  .then((group) => {
                    toast.show(`Created “${group.name}”`, {
                      tone: "success",
                      detail: "It grants nothing yet. Open it to give it access to a business unit.",
                    });
                    setCreating(false);
                    setNewName("");
                    setNewDescription("");
                    reload();
                    setOpenGroup(group.id);
                  })
                  .catch((cause: unknown) => {
                    const failure =
                      cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
                    toast.show("The group was not created", { tone: "danger", detail: failure.userMessage });
                  })
                  .finally(() => setBusy(false));
              }}
            >
              Create
            </Button>
          </>
        }
      >
        <TextField label="Name" value={newName} onChange={setNewName} required />
        <TextField
          label="Description"
          value={newDescription}
          onChange={setNewDescription}
          hint="What this group is for, in one line. A group nobody can explain is a group nobody dares delete."
        />
      </Modal>

      <Modal
        open={deleting !== null}
        role="alertdialog"
        title={deleting === null ? "Delete group" : `Delete “${deleting.group.name}”`}
        onClose={() => setDeleting(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                if (deleting === null) return;
                const name = deleting.group.name;
                setBusy(true);
                ports
                  .deleteGroup(deleting.group.id)
                  .then(() => {
                    toast.show(`Deleted “${name}”`, {
                      tone: "success",
                      detail: "The policy trail keeps its record; the group itself is gone.",
                    });
                    setDeleting(null);
                    reload();
                  })
                  .catch((cause: unknown) => {
                    const failure =
                      cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
                    toast.show("The group was not deleted", { tone: "danger", detail: failure.userMessage });
                  })
                  .finally(() => setBusy(false));
              }}
            >
              Delete the group
            </Button>
          </>
        }
      >
        {/* The blast radius, stated before the act rather than reported after it. Deleting a carrier is the
            fastest way to remove access from many people at once, and the module contract makes naming its
            effect an explicit requirement. */}
        <p>{deleting === null ? "" : blastRadius(deleting, buName)}</p>
        <p className="field__hint">
          A group cannot be restored. Its entries in the policy trail deliberately outlive it, so what it
          conferred stays on the record.
        </p>
      </Modal>
    </Page>
  );
}

const estateColumns: readonly Column<FacetEntry>[] = [
  {
    key: "groupName",
    header: "Source group",
    type: "text",
    value: (entry) => entry.groupName,
    render: (entry) => <Badge tone="info">{entry.groupName}</Badge>,
  },
  { key: "facet", header: "Facet", type: "text", value: (entry) => entry.facet },
  { key: "scope", header: "Business unit", type: "text", value: (entry) => entry.scope },
  {
    key: "detail",
    header: "What it says",
    type: "text",
    value: (entry) => entry.detail,
    render: (entry) => <span className="mono">{entry.detail}</span>,
  },
  {
    key: "effect",
    header: "Effect",
    type: "text",
    value: (entry) => entry.effect,
    render: (entry) => (
      <Badge tone={entry.effect === "denies" ? "danger" : entry.effect === "hides" ? "warning" : "success"}>
        {humanise(entry.effect)}
      </Badge>
    ),
  },
];

/* ---------------------------------------------------------------------------------------------------
 * The policy trail
 * ------------------------------------------------------------------------------------------------- */

/**
 * Who changed the access model, and when.
 *
 * This is a different log from the Audit screen's, and the difference matters: the audit trail is scoped to
 * a business unit and records what happened *inside* one, while these are instance-level acts on the
 * permission model itself. Reading it belongs to the access section, which is why it lives here.
 */
function PolicyTrail({
  entries,
  buName,
}: {
  readonly entries: readonly PolicyAuditRow[];
  readonly buName: (id: string) => string;
}): JSX.Element {
  const columns: readonly Column<PolicyAuditRow>[] = [
    {
      key: "at",
      header: "When",
      type: "date",
      value: (row) => row.at,
      render: (row) => <time title={row.at}>{formatDateTime(row.at)}</time>,
    },
    { key: "action", header: "What", type: "text", value: (row) => row.action },
    {
      key: "actor_email",
      header: "Who",
      type: "text",
      value: (row) => row.actor_email ?? "",
      render: (row) => row.actor_email ?? <span className="field__hint">—</span>,
    },
    {
      key: "bu_id",
      header: "Business unit",
      type: "text",
      value: (row) => (row.bu_id === null ? "" : buName(row.bu_id)),
      render: (row) => (row.bu_id === null ? <span className="field__hint">Whole instance</span> : <span>{buName(row.bu_id)}</span>),
      hideOnMobile: true,
    },
    {
      key: "detail",
      header: "Detail",
      type: "text",
      value: (row) => row.detail ?? "",
      render: (row) => <span className="mono">{row.detail ?? "—"}</span>,
      hideOnMobile: true,
    },
  ];

  return (
    <Table
      caption="Changes to the access model"
      columns={columns}
      rows={entries}
      rowKey={(row) => row.id}
      defaultSort={{ key: "at", direction: "descending" }}
      titleKey="action"
      mobile="cards"
      empty={{
        title: "No changes recorded",
        body: "Every group created, renamed, granted or deleted is recorded here, with who did it. Entries appear as soon as somebody changes the access model.",
      }}
    />
  );
}
